/**
 * Payments Zustand store.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Payment,
  PaymentsSummary,
  CreatePaymentInput,
  ListPaymentsFilters,
  listPayments,
  getPayment,
  createPayment,
  refundPayment,
  getPaymentsSummary,
} from '../services/paymentsApi';

interface PaymentsState {
  items: Payment[];
  pagination: { total: number; page: number; limit: number; pages: number } | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  current: Payment | null;
  summary: PaymentsSummary | null;
  filters: ListPaymentsFilters;

  setFilters: (patch: Partial<ListPaymentsFilters>) => void;
  fetchList: () => Promise<void>;
  fetchOne: (id: number | string) => Promise<Payment>;
  fetchSummary: () => Promise<void>;
  record: (input: CreatePaymentInput) => Promise<Payment>;
  refund: (id: number | string, reason?: string) => Promise<Payment>;
  reset: () => void;
}

const defaultFilters: ListPaymentsFilters = { page: 1, limit: 20 };

const initial = {
  items: [] as Payment[],
  pagination: null as PaymentsState['pagination'],
  loading: false,
  saving: false,
  error: null as string | null,
  current: null as Payment | null,
  summary: null as PaymentsSummary | null,
  filters: { ...defaultFilters },
};

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const usePaymentsStore = create<PaymentsState>()(
  devtools(
    (set, get) => ({
      ...initial,

      setFilters: (patch) => set({ filters: { ...get().filters, ...patch, page: 1 } }),

      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const result = await listPayments(get().filters);
          set({ items: result.data, pagination: result.pagination, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },

      fetchOne: async (id) => {
        set({ loading: true, error: null });
        try {
          const p = await getPayment(id);
          set({ current: p, loading: false });
          return p;
        } catch (err) {
          set({ loading: false, error: toErr(err) });
          throw err;
        }
      },

      fetchSummary: async () => {
        try {
          const summary = await getPaymentsSummary();
          set({ summary });
        } catch (err) {
          set({ error: toErr(err) });
        }
      },

      record: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createPayment(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },

      refund: async (id, reason) => {
        set({ saving: true, error: null });
        try {
          const refund = await refundPayment(id, reason);
          set((s) => ({ items: [refund, ...s.items], saving: false }));
          return refund;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },

      reset: () => set({ ...initial, filters: { ...defaultFilters } }),
    }),
    { name: 'payments-store' }
  )
);
